import { useGet } from "@albertine/api-client";
import type { CatalogItem, Theme } from "@albertine/types/openapi/lmt";
import { CatalogItemSchema } from "@albertine/types/openapi/lmt";
import * as Sentry from "@sentry/react";
import type { Tenant } from "albertine-shared-web";
import z from "zod";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

export type ThemeArticlesSource =
    | { type: "memberPreview"; memberId: string }
    | { type: "tenantContent"; tenant?: Tenant };

type WithThemeAndArticlesProps = {
    themeId: string;
    articlesSource: ThemeArticlesSource;
    skipThemeLoadWith?: Theme;
    renderLoading?: React.JSX.Element;
    renderOnError: React.JSX.Element;
    children: (theme: Theme, articles: CatalogItem[]) => React.JSX.Element;
};

function WithThemeAndArticles(props: WithThemeAndArticlesProps) {
    const {
        themeId,
        articlesSource,
        skipThemeLoadWith,
        renderOnError,
        renderLoading,
        children,
    } = props;

    const themeQuery = useGet(
        "theme",
        {
            param: { themeId },
        },
        {
            enabled: !!themeId && !skipThemeLoadWith,
        },
    );

    const tenantContentCatalogItems = useGet(
        "lmtCatalogThemeCatalogItems",
        {
            param: { themeId },
            query: {
                tenant:
                    articlesSource.type === "tenantContent"
                        ? articlesSource.tenant
                        : undefined,
            },
        },
        {
            enabled: !!themeId && articlesSource.type === "tenantContent",
        },
    );

    const memberPreviewCatalogItems = useGet(
        "previewMemberThemeContent",
        {
            param: {
                themeId,
                memberId:
                    articlesSource.type === "memberPreview"
                        ? articlesSource.memberId
                        : "",
            },
        },
        {
            enabled: !!themeId && articlesSource.type === "memberPreview",
        },
    );

    const theme = skipThemeLoadWith ?? themeQuery.data;

    if (themeQuery.isError) {
        Sentry.captureException(themeQuery.error, {
            extra: {
                message: `WithThemeAndArticles: get theme failed for id ${themeId}`,
            },
        });
        return renderOnError;
    }

    const loading = renderLoading || <LoadingSpinner />;
    const catalogItemsLoading =
        articlesSource.type === "tenantContent"
            ? tenantContentCatalogItems.isLoading
            : memberPreviewCatalogItems.isLoading;

    if ((!skipThemeLoadWith && themeQuery.isLoading) || catalogItemsLoading) {
        return loading;
    }

    const catalogItems =
        articlesSource.type === "tenantContent"
            ? tenantContentCatalogItems.data?.catalogItems
            : memberPreviewCatalogItems.data?.catalogItems;

    if (!theme || !catalogItems) {
        return null;
    }

    const parsedCatalogItems: CatalogItem[] = z
        .array(CatalogItemSchema)
        .parse(catalogItems);
    return children(theme, parsedCatalogItems);
}

export default WithThemeAndArticles;
